/**
 * Shared API service for KuzFit API calls and postMessage communication
 */

const API_BASE_URL = 'https://api.kuzfit.com/kuzfit';

/**
 * Call the getUserString API and send result to parent window
 * @param {string} accessToken - Auth0 access token
 * @param {boolean} isAuthenticated - Whether user is authenticated
 */
export const callUserStringApi = async (accessToken, isAuthenticated) => {
  console.log('Calling API...');
  
  // If user is not authenticated, send status message immediately
  if (!isAuthenticated) {
    console.log('User not authenticated, sending logout status');
    sendPostMessage({
      userString: '',
      apiResponse: null,
      error: 'User not authenticated',
      status: 'logged_out'
    });
    return;
  }
  
  try {
    // Call API to get userString with authentication
    const response = await fetch(`${API_BASE_URL}/getUserString`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        'ngrok-skip-browser-warning': 'true'
      }
    });

    console.log('API response status:', response.status);
    
    if (response.ok) {
      const responseText = await response.text();
      console.log('API response text:', responseText);
      
      // Send to parent WordPress site via postMessage
      sendPostMessage({
        userString: responseText,
        apiResponse: responseText,
        status: 'authenticated'
      });
    } else {
      console.error('API call failed:', response.status, response.statusText);
      // Send postMessage with empty string when API fails
      sendPostMessage({
        userString: '',
        apiResponse: null,
        error: `API failed: ${response.status}`,
        status: 'api_error'
      });
    }
  } catch (error) {
    console.error('Error calling API:', error);
    // Send postMessage with empty string when authentication or other errors occur
    sendPostMessage({
      userString: '',
      apiResponse: null,
      error: error.message,
      status: 'error'
    });
  }
};

/**
 * Send postMessage to parent window
 * @param {Object} data - Data to send
 */
export const sendPostMessage = (data) => {
  if (window.parent) {
    const message = {
      type: 'USER_STRING_UPDATE',
      ...data
    };
    
    console.log('Sending postMessage to parent window:', message);
    window.parent.postMessage(message, '*');
  } else {
    console.log('No parent window found for postMessage');
  }
};

/**
 * Set up polling for the API
 * @param {Function} apiCallFunction - Function to call the API
 * @param {number} intervalMs - Polling interval in milliseconds
 * @returns {Object} Object with start and stop methods
 */
export const createApiPoller = (apiCallFunction, intervalMs = 60000) => {
  let intervalRef = null;
  
  const start = () => {
    if (intervalRef) {
      console.log('Polling already started');
      return;
    }
    
    console.log(`Starting API polling every ${intervalMs / 1000} seconds`);
    intervalRef = setInterval(() => {
      console.log('Polling API...');
      apiCallFunction();
    }, intervalMs);
  };
  
  const stop = () => {
    if (intervalRef) {
      clearInterval(intervalRef);
      intervalRef = null;
      console.log('Polling stopped');
    }
  };
  
  return { start, stop };
};

/**
 * Fetch all organisations from KuzFit API
 * @param {string} accessToken - optional Auth0 access token to pass through
 * @returns {Promise<any>} Parsed JSON response
 */
export const fetchAllOrganisations = async (accessToken) => {
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(`${API_BASE_URL}/organisationReadAll`, {
    method: 'GET',
    headers
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`organisationReadAll failed: ${response.status} ${text}`);
  }

  return response.json();
};

/**
 * Create a new organisation via KuzFit API
 * @param {Object} payload - Body matching DC_Create_Org schema
 * @param {string} accessToken - optional Auth0 access token to pass through
 * @returns {Promise<any>} Parsed JSON response
 */
export const createOrganisation = async (payload, accessToken) => {
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(`${API_BASE_URL}/Create_Organisation`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload || {})
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Create_Organisation failed: ${response.status} ${text}`);
  }

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json') || contentType.includes('text/json')) {
    return response.json();
  }
  return response.text();
};

/**
 * Fetch all photos from KuzFit API
 * @param {string} accessToken - optional Auth0 access token to pass through
 * @returns {Promise<any>} Parsed JSON response with photo URLs
 */
export const fetchAllPhotos = async (accessToken) => {
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(`${API_BASE_URL}/photoGetAll`, {
    method: 'GET',
    headers
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`photoGetAll failed: ${response.status} ${text}`);
  }

  return response.json();
};

/**
 * Delete a photo via KuzFit API
 * @param {string} photoId - ID of the photo to delete
 * @param {string} accessToken - optional Auth0 access token to pass through
 * @returns {Promise<any>} Parsed JSON response
 */
export const photoDelete = async (photoId, accessToken) => {
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(`${API_BASE_URL}/photoDelete?fileId=${photoId}`, {
    method: 'DELETE',
    headers
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`photoDelete failed: ${response.status} ${text}`);
  }

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json') || contentType.includes('text/json')) {
    return response.json();
  }
  return response.text();
};

/**
 * Upload photo to KuzFit API
 * @param {File} file - File to upload
 * @param {string} accessToken - optional Auth0 access token to pass through
 * @param {Array<string>} params - optional parameters array
 * @returns {Promise<any>} Parsed response
 */
export const photoUpload = async (file, accessToken, params = []) => {
  const headers = {
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const formData = new FormData();
  formData.append('Files', file);
  
  // Add params if provided
  params.forEach(param => {
    formData.append('Params', param);
  });

  const response = await fetch(`${API_BASE_URL}/photoUpload`, {
    method: 'POST',
    headers,
    body: formData
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`photoUpload failed: ${response.status} ${text}`);
  }

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json') || contentType.includes('text/json')) {
    return response.json();
  }
  return response.text();
};

/**
 * Get image content from KuzFit API
 * @param {number} fileId - ID of the file to retrieve
 * @param {string} accessToken - optional Auth0 access token to pass through
 * @returns {Promise<Blob>} Image blob data
 */
export const getImage = async (fileId, accessToken) => {
  const headers = {
    'ngrok-skip-browser-warning': 'true'
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(`${API_BASE_URL}/getImage?id=${fileId}`, {
    method: 'GET',
    headers
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`getImage failed: ${response.status} ${text}`);
  }

  return response.blob();
};
